import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import PhotoGallery from '../../components/property/PhotoGallery';
import AgencyProfileEditor from '../../components/agent/AgencyProfileEditor';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import PropertyChat, { ChatList } from '../../components/PropertyChat';
import VirtualTourGenerator from '../../components/agent/VirtualTourGenerator';
import TwoFactorSetup from '../../components/TwoFactorSetup';
import {
  Home, Building, Plus, Calculator, Users, BarChart3, User, LogOut, Menu,
  MapPin, Calendar, Clock, Eye, Check, X, Gavel, Edit, Trash2, ChevronRight, Send, Bell,
  CreditCard, Crown, CheckCircle, AlertCircle, Loader2, ExternalLink, Download, TrendingUp,
  Star, FileText, Award, DollarSign, Target, Camera, Grid3X3, Briefcase, MessageSquare, Video, Shield,
  ArrowLeft, Save, Settings
} from 'lucide-react';

// ============ PROPERTY FORM CONTEXT ============
// This context persists form data across navigation
const PropertyFormContext = createContext(null);

const initialPropertyFormData = {
  title: '',
  description: '',
  address: '',
  city: '',
  area: '',
  omi_zone: '',
  property_type: 'appartamento',
  sqm: '',
  rooms: '',
  floor: '',
  property_condition: 'buono',
  energy_class: '',
  images: [],
  auction_start_price: '',
  reserve_price: '',
  bid_increment: '1000',
  deposit_amount: '',
  auction_date: '',
  auction_time: '',
  // OMI valuation data
  omi_valuation_result: null
};

export const PropertyFormProvider = ({ children }) => {
  const [formData, setFormData] = useState(() => {
    // Try to restore from sessionStorage
    const saved = sessionStorage.getItem('propertyFormData');
    return saved ? JSON.parse(saved) : initialPropertyFormData;
  });
  
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = sessionStorage.getItem('propertyFormStep');
    return saved ? parseInt(saved) : 1;
  });

  // Persist to sessionStorage on changes
  useEffect(() => {
    sessionStorage.setItem('propertyFormData', JSON.stringify(formData));
  }, [formData]);
  
  useEffect(() => {
    sessionStorage.setItem('propertyFormStep', currentStep.toString());
  }, [currentStep]);

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const resetForm = () => {
    setFormData(initialPropertyFormData);
    setCurrentStep(1);
    sessionStorage.removeItem('propertyFormData');
    sessionStorage.removeItem('propertyFormStep');
  };

  // Apply OMI valuation to form
  const applyOMIValuation = (result) => {
    setFormData(prev => ({
      ...prev,
      auction_start_price: result.auction_start_price?.toString() || '',
      bid_increment: result.suggested_bid_increment?.toString() || '1000',
      omi_valuation_result: result
    }));
  };

  return (
    <PropertyFormContext.Provider value={{
      formData,
      setFormData,
      updateFormData,
      currentStep,
      setCurrentStep,
      resetForm,
      applyOMIValuation
    }}>
      {children}
    </PropertyFormContext.Provider>
  );
};

export const usePropertyForm = () => {
  const context = useContext(PropertyFormContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      formData: initialPropertyFormData,
      setFormData: () => {},
      updateFormData: () => {},
      currentStep: 1,
      setCurrentStep: () => {},
      resetForm: () => {},
      applyOMIValuation: () => {}
    };
  }
  return context;
};

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { path: '/agent', icon: Home, label: 'Dashboard', exact: true },
    { path: '/agent/profilo-agenzia', icon: Briefcase, label: 'Profilo Agenzia' },
    { path: '/agent/immobili', icon: Building, label: 'I Miei Immobili' },
    { path: '/agent/disponibili', icon: MapPin, label: 'Immobili Disponibili' },
    { path: '/agent/proposte', icon: Send, label: 'Le Mie Proposte' },
    { path: '/agent/messaggi', icon: MessageSquare, label: 'Messaggi' },
    { path: '/agent/valutazione', icon: Calculator, label: 'Valutazione OMI' },
    { path: '/agent/visite', icon: Calendar, label: 'Richieste Visite' },
    { path: '/agent/partecipazioni', icon: Users, label: 'Partecipazioni' },
    { path: '/agent/notifiche', icon: Bell, label: 'Notifiche' },
    { path: '/agent/abbonamento', icon: CreditCard, label: 'Abbonamento' },
    { path: '/agent/report', icon: BarChart3, label: 'Report' },
    { path: '/agent/profilo', icon: User, label: 'Profilo Azienda' },
    { path: '/agent/sicurezza', icon: Shield, label: 'Sicurezza' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsOpen(false)} />
      )}

      <aside className={`fixed top-0 left-0 h-full w-72 bg-[#F5F5F7] z-50 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6">
            <Link to="/" className="flex items-center">
              <img 
                src="/weqasa-logo.png" 
                alt="WEQASA" 
                className="h-10 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Agency Info */}
          <div className="px-6 pb-6">
            <div className="flex items-center space-x-3 p-4 bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
              <div className="w-10 h-10 bg-[#0071e3]/10 rounded-xl flex items-center justify-center">
                <Building className="h-5 w-5 text-[#0071e3]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-[#1D1D1F] truncate">{user?.company_name || 'Agenzia'}</p>
                <span className={`inline-flex items-center text-[11px] font-medium ${user?.verification_status === 'approved' ? 'text-green-600' : 'text-amber-600'}`}>
                  {user?.verification_status === 'approved' ? 'Verificato' : 'In verifica'}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive(item.path, item.exact)
                    ? 'bg-white text-[#1D1D1F] shadow-[0_2px_8px_rgb(0,0,0,0.04)]'
                    : 'text-[#86868B] hover:bg-white/50 hover:text-[#1D1D1F]'
                }`}
              >
                <item.icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="text-[14px] font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-4 py-3 w-full text-[#86868B] hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
              <span className="text-[14px] font-medium">Esci</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Overview
const AgentOverview = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ properties: 0, visits: 0, participations: 0, activeAuctions: 0 });
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propsRes, visitsRes, partsRes] = await Promise.all([
        axios.get('/agent/properties'),
        axios.get('/visits/agent'),
        axios.get('/participation/agent')
      ]);

      setProperties(propsRes.data.slice(0, 5));
      setStats({
        properties: propsRes.data.length,
        visits: visitsRes.data.filter(v => v.status === 'pending').length,
        participations: partsRes.data.filter(p => p.participation_status === 'pending').length,
        activeAuctions: propsRes.data.filter(p => p.auction_status === 'active').length
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  return (
    <div className="space-y-8" data-testid="agent-dashboard">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#1D1D1F]">Dashboard</h1>
          <p className="text-[15px] text-[#86868B] mt-1">Benvenuto, {user?.first_name}</p>
        </div>
        <Link to="/agent/immobili/nuovo">
          <button className="btn-apple-primary">
            <Plus className="h-4 w-4 mr-2" strokeWidth={2} />
            Nuovo Immobile
          </button>
        </Link>
      </div>

      {/* Verification Warning */}
      {user?.verification_status !== 'approved' && (
        <div className="flex items-center gap-4 bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
          </div>
          <p className="text-[15px] text-amber-800">
            Il tuo account è in attesa di verifica. Alcune funzionalità potrebbero essere limitate.
          </p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Building, value: stats.properties, label: 'Immobili', color: 'bg-[#1D1D1F]' },
          { icon: Calendar, value: stats.visits, label: 'Visite in attesa', color: 'bg-blue-500' },
          { icon: Users, value: stats.participations, label: 'Richieste', color: 'bg-amber-500' },
          { icon: TrendingUp, value: stats.activeAuctions, label: 'Gare attive', color: 'bg-green-500' },
        ].map((stat, index) => (
          <div key={index} className="premium-card p-6">
            <div className={`w-12 h-12 ${stat.color} rounded-2xl flex items-center justify-center mb-4`}>
              <stat.icon className="h-6 w-6 text-white" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-bold text-[#1D1D1F] tracking-tight">{stat.value}</div>
            <div className="text-[13px] text-[#86868B] mt-1">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Recent Properties */}
      <div className="premium-card overflow-hidden">
        <div className="px-6 py-5 flex justify-between items-center border-b border-black/5">
          <h2 className="text-lg font-semibold text-[#1D1D1F] tracking-tight">I Miei Immobili</h2>
          <Link to="/agent/immobili" className="text-[14px] font-medium text-[#0A2540] hover:text-[#061A2D] transition-colors flex items-center gap-1">
            Vedi tutti
            <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
        {properties.length > 0 ? (
          <div className="divide-y divide-black/5">
            {properties.map((prop) => (
              <div key={prop.property_id} className="p-5 flex items-center justify-between hover:bg-[#F5F5F7]/50 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-[#F5F5F7] rounded-xl overflow-hidden flex-shrink-0">
                    <img 
                      src={prop.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=100'} 
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-semibold text-[#1D1D1F]">{prop.title}</h3>
                    <p className="text-[13px] text-[#86868B] mt-0.5">{prop.city} • {formatPrice(prop.auction_start_price)}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-3 py-1 text-[11px] font-semibold rounded-full ${
                  prop.auction_status === 'active' ? 'bg-green-50 text-green-700' :
                  prop.auction_status === 'scheduled' ? 'bg-blue-50 text-blue-700' :
                  prop.auction_status === 'draft' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {prop.auction_status === 'active' ? 'Gara Attiva' : 
                   prop.auction_status === 'scheduled' ? 'Programmata' :
                   prop.auction_status === 'draft' ? 'Bozza' : prop.auction_status || 'Bozza'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-[#F5F5F7] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building className="h-8 w-8 text-[#86868B]" strokeWidth={1.5} />
            </div>
            <p className="text-[15px] text-[#86868B]">Nessun immobile pubblicato</p>
            <Link to="/agent/immobili/nuovo" className="text-[14px] font-medium text-[#0A2540] hover:text-[#061A2D] mt-2 inline-block">
              Crea il tuo primo immobile
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// Properties Management
const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/agent/properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[#1D1D1F] ">I Miei Immobili</h1>
        <Link to="/agent/immobili/nuovo">
          <Button className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Immobile
          </Button>
        </Link>
      </div>

      {properties.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((prop) => (
            <div key={prop.property_id} className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
              <div className="aspect-video bg-gray-100">
                <img 
                  src={prop.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400'} 
                  alt={prop.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-[#1D1D1F]">{prop.title}</h3>
                  <Badge className={
                    prop.auction_status === 'active' ? 'badge-active' :
                    prop.auction_status === 'scheduled' ? 'badge-auction' :
                    prop.auction_status === 'published' ? 'badge-pending' : 'badge-closed'
                  }>
                    {prop.auction_status || 'Bozza'}
                  </Badge>
                </div>
                <p className="text-sm text-[#86868B] flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {prop.city}
                </p>
                <p className="text-lg font-semibold text-[#0071e3] mt-2">
                  {formatPrice(prop.auction_start_price)}
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Link to={`/agent/immobili/${prop.property_id}`} className="flex-1">
                    <Button variant="outline" className="w-full" size="sm">
                      <Edit className="h-4 w-4 mr-1" />
                      Modifica
                    </Button>
                  </Link>
                  <Link to={`/immobili/${prop.property_id}`}>
                    <Button variant="outline" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <div className="mt-2">
                  <VirtualTourGenerator
                    propertyId={prop.property_id}
                    propertyTitle={prop.title}
                    existingImages={prop.images || []}
                    existingFloorplans={prop.floorplans || []}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <Building className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessun immobile</h3>
          <p className="text-[#86868B] mb-6">Inizia aggiungendo il tuo primo immobile</p>
          <Link to="/agent/immobili/nuovo">
            <Button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi Immobile
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

// Create Property Page
const CreatePropertyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { formData, setFormData, updateFormData, currentStep, setCurrentStep, resetForm } = usePropertyForm();

  const handleChange = (e) => {
    updateFormData({ [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Create property
      const propRes = await axios.post('/properties', {
        title: formData.title,
        description: formData.description,
        address: formData.address,
        city: formData.city,
        area: formData.area,
        omi_zone: formData.omi_zone,
        property_type: formData.property_type,
        sqm: parseFloat(formData.sqm),
        rooms: parseInt(formData.rooms) || 0,
        floor: formData.floor ? parseInt(formData.floor) : null,
        property_condition: formData.property_condition,
        energy_class: formData.energy_class,
        images: formData.images
      });

      // Setup auction if prices are set
      if (formData.auction_start_price && formData.auction_date) {
        await axios.post(`/properties/${propRes.data.property_id}/auction`, {
          property_id: propRes.data.property_id,
          auction_start_price: parseFloat(formData.auction_start_price),
          reserve_price: parseFloat(formData.reserve_price) || parseFloat(formData.auction_start_price),
          bid_increment: parseFloat(formData.bid_increment),
          deposit_amount: parseFloat(formData.deposit_amount) || parseFloat(formData.auction_start_price) * 0.05,
          auction_date: formData.auction_date,
          auction_time: formData.auction_time || '10:00',
          auction_duration: 60
        });
      }

      toast.success('Immobile creato con successo!');
      resetForm(); // Clear form data after successful creation
      navigate('/agent/immobili');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  // Step indicator component
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {[1, 2, 3].map((stepNum) => (
        <React.Fragment key={stepNum}>
          <button
            onClick={() => setCurrentStep(stepNum)}
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              currentStep === stepNum
                ? 'bg-[#0071e3] text-white'
                : currentStep > stepNum
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {currentStep > stepNum ? <CheckCircle className="w-5 h-5" /> : stepNum}
          </button>
          {stepNum < 3 && (
            <div className={`w-16 h-1 mx-2 rounded ${currentStep > stepNum ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/agent/immobili')} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-semibold text-[#1D1D1F]">Nuovo Immobile</h1>
        </div>
        <div className="text-sm text-gray-500">
          Step {currentStep} di 3
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <StepIndicator />
        
        {/* Step 1: Basic Info */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-[#1D1D1F]">Informazioni Base</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Titolo Annuncio *</Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="es. Elegante appartamento in centro storico"
                  value={formData.title}
                  onChange={handleChange}
                  data-testid="property-title"
                />
              </div>

              <div>
                <Label htmlFor="description">Descrizione *</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Descrivi l'immobile..."
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  data-testid="property-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">Città *</Label>
                  <Input id="city" name="city" value={formData.city} onChange={handleChange} data-testid="property-city" />
                </div>
                <div>
                  <Label htmlFor="area">Zona/Quartiere</Label>
                  <Input id="area" name="area" value={formData.area} onChange={handleChange} />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Indirizzo *</Label>
                <Input id="address" name="address" value={formData.address} onChange={handleChange} data-testid="property-address" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="omi_zone">Zona OMI</Label>
                  <Input id="omi_zone" name="omi_zone" placeholder="es. Centro" value={formData.omi_zone} onChange={handleChange} />
                </div>
                <div>
                  <Label htmlFor="property_type">Tipologia *</Label>
                  <Select value={formData.property_type} onValueChange={(v) => updateFormData({ property_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appartamento">Appartamento</SelectItem>
                      <SelectItem value="villa">Villa</SelectItem>
                      <SelectItem value="casa">Casa Indipendente</SelectItem>
                      <SelectItem value="attico">Attico</SelectItem>
                      <SelectItem value="ufficio">Ufficio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => navigate('/agent/immobili')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button onClick={() => setCurrentStep(2)} className="btn-primary">
                Continua
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Property Details */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-[#1D1D1F]">Caratteristiche</h2>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="sqm">Superficie (m²) *</Label>
                <Input id="sqm" name="sqm" type="number" value={formData.sqm} onChange={handleChange} data-testid="property-sqm" />
              </div>
              <div>
                <Label htmlFor="rooms">Locali</Label>
                <Input id="rooms" name="rooms" type="number" value={formData.rooms} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="floor">Piano</Label>
                <Input id="floor" name="floor" type="number" value={formData.floor} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="energy_class">Classe Energetica</Label>
                <Select value={formData.energy_class} onValueChange={(v) => updateFormData({ energy_class: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    {['A4', 'A3', 'A2', 'A1', 'B', 'C', 'D', 'E', 'F', 'G'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Condizione *</Label>
              <div className="grid grid-cols-3 gap-4 mt-2">
                {[
                  { value: 'ottimo', label: 'Ottimo' },
                  { value: 'buono', label: 'Buono' },
                  { value: 'da_ristrutturare', label: 'Da Ristrutturare' }
                ].map((cond) => (
                  <button
                    key={cond.value}
                    type="button"
                    onClick={() => updateFormData({ property_condition: cond.value })}
                    className={`p-4 rounded-lg border text-center transition-colors ${
                      formData.property_condition === cond.value
                        ? 'border-[#0071e3] bg-[#0071e3]/5 text-[#0071e3]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {cond.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
              <Button onClick={() => setCurrentStep(3)} className="btn-primary">
                Continua
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Auction Setup */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-[#1D1D1F]">Configurazione Gara di Vendita</h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                Usa lo strumento di{' '}
                <Link to="/agent/valutazione" className="underline font-medium">
                  Valutazione OMI
                </Link>{' '}
                per calcolare il prezzo consigliato. I dati del form verranno mantenuti.
              </p>
            </div>

            {/* Show OMI valuation result if available */}
            {formData.omi_valuation_result && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-green-800 font-medium mb-2">
                  <CheckCircle className="w-5 h-5" />
                  Valutazione OMI applicata
                </div>
                <p className="text-sm text-green-700">
                  Valore stimato: €{formData.omi_valuation_result.estimated_market_value?.toLocaleString()} | 
                  Prezzo base suggerito: €{formData.omi_valuation_result.auction_start_price?.toLocaleString()}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="auction_start_price">Prezzo Base Gara (€) *</Label>
                <Input id="auction_start_price" name="auction_start_price" type="number" value={formData.auction_start_price} onChange={handleChange} data-testid="property-price" />
              </div>
              <div>
                <Label htmlFor="reserve_price">Prezzo Minimo (nascosto) €</Label>
                <Input id="reserve_price" name="reserve_price" type="number" value={formData.reserve_price} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="bid_increment">Rilancio Minimo (€)</Label>
                <Input id="bid_increment" name="bid_increment" type="number" value={formData.bid_increment} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="deposit_amount">Caparra (€)</Label>
                <Input id="deposit_amount" name="deposit_amount" type="number" placeholder="5% del prezzo base" value={formData.deposit_amount} onChange={handleChange} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="auction_date">Data Gara</Label>
                <Input id="auction_date" name="auction_date" type="date" value={formData.auction_date} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="auction_time">Ora Gara</Label>
                <Input id="auction_time" name="auction_time" type="time" value={formData.auction_time} onChange={handleChange} />
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
              <Button onClick={handleSubmit} disabled={loading} className="btn-primary" data-testid="create-property-submit">
                {loading ? 'Creazione...' : 'Crea Immobile'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// OMI Valuation Tool
const OMIValuationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [zones, setZones] = useState({});
  const [formData, setFormData] = useState({
    city: '',
    omi_zone: '',
    property_type: 'appartamento',
    property_condition: 'buono',
    sqm: ''
  });
  const [result, setResult] = useState(null);
  
  // Access the property form context
  const { formData: propertyFormData, applyOMIValuation, currentStep } = usePropertyForm();
  
  // Pre-fill from property form if available
  useEffect(() => {
    if (propertyFormData.city || propertyFormData.sqm) {
      setFormData(prev => ({
        ...prev,
        city: propertyFormData.city || prev.city,
        omi_zone: propertyFormData.omi_zone || prev.omi_zone,
        property_type: propertyFormData.property_type || prev.property_type,
        property_condition: propertyFormData.property_condition || prev.property_condition,
        sqm: propertyFormData.sqm || prev.sqm
      }));
    }
  }, [propertyFormData]);

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await axios.get('/omi/zones');
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const calculateValuation = async () => {
    if (!formData.city || !formData.omi_zone || !formData.sqm) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/omi/valuation', {
        city: formData.city,
        omi_zone: formData.omi_zone,
        property_type: formData.property_type,
        property_condition: formData.property_condition,
        sqm: parseFloat(formData.sqm)
      });
      setResult(response.data);
    } catch (error) {
      toast.error('Errore nel calcolo della valutazione');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const handleSaveAndReturn = () => {
    if (result) {
      applyOMIValuation(result);
      toast.success('Valutazione salvata! Prezzi applicati al form.');
      navigate('/agent/nuovo-immobile');
    }
  };

  // Check if we came from the property form
  const hasPropertyFormData = propertyFormData.title || propertyFormData.city;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-[#1D1D1F]">Valutazione OMI</h1>
            <p className="text-gray-600">Calcola il valore di mercato stimato basandosi sui dati OMI</p>
          </div>
        </div>
        {hasPropertyFormData && (
          <Badge className="bg-blue-100 text-blue-800">
            Dati da form immobile
          </Badge>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6 space-y-4">
          <h2 className="font-semibold text-lg">Dati Immobile</h2>

          <div>
            <Label>Città *</Label>
            <Select value={formData.city} onValueChange={(v) => setFormData({...formData, city: v, omi_zone: ''})}>
              <SelectTrigger data-testid="omi-city">
                <SelectValue placeholder="Seleziona città" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(zones).map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Zona OMI *</Label>
            <Select 
              value={formData.omi_zone} 
              onValueChange={(v) => setFormData({...formData, omi_zone: v})}
              disabled={!formData.city}
            >
              <SelectTrigger data-testid="omi-zone">
                <SelectValue placeholder="Seleziona zona" />
              </SelectTrigger>
              <SelectContent>
                {(zones[formData.city] || []).map(zone => (
                  <SelectItem key={zone} value={zone}>{zone}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipologia *</Label>
              <Select value={formData.property_type} onValueChange={(v) => setFormData({...formData, property_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appartamento">Appartamento</SelectItem>
                  <SelectItem value="villa">Villa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Condizione *</Label>
              <Select value={formData.property_condition} onValueChange={(v) => setFormData({...formData, property_condition: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ottimo">Ottimo</SelectItem>
                  <SelectItem value="buono">Buono</SelectItem>
                  <SelectItem value="da_ristrutturare">Da Ristrutturare</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Superficie (m²) *</Label>
            <Input 
              type="number" 
              value={formData.sqm} 
              onChange={(e) => setFormData({...formData, sqm: e.target.value})}
              data-testid="omi-sqm"
            />
          </div>

          <Button onClick={calculateValuation} disabled={loading} className="w-full btn-primary" data-testid="calculate-omi-btn">
            {loading ? 'Calcolo...' : 'Calcola Valutazione'}
          </Button>
        </div>

        {/* Results */}
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
          <h2 className="font-semibold text-lg mb-4">Risultato Valutazione</h2>
          
          {result ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-[#86868B]">Valore OMI Min</div>
                  <div className="text-lg font-semibold">{formatPrice(result.omi_min_value)}/m²</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-[#86868B]">Valore OMI Max</div>
                  <div className="text-lg font-semibold">{formatPrice(result.omi_max_value)}/m²</div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="text-sm text-[#86868B] mb-2">Valore Medio €/m²</div>
                <div className="text-2xl font-semibold text-[#1D1D1F]">{formatPrice(result.avg_price_sqm)}</div>
              </div>

              <div className="bg-[#0071e3]/5 p-4 rounded-lg border border-[#0071e3]/20">
                <div className="text-sm text-[#0071e3] mb-1">Valore di Mercato Stimato</div>
                <div className="text-3xl font-bold text-[#0071e3]">{formatPrice(result.estimated_market_value)}</div>
              </div>

              <div className="bg-[#0071e3]/10 p-4 rounded-lg border border-[#0071e3]/30">
                <div className="text-sm text-[#0071e3] mb-1">Prezzo Base Gara Consigliato (-15%)</div>
                <div className="text-2xl font-bold text-[#0071e3]">{formatPrice(result.auction_start_price)}</div>
              </div>

              <div className="text-sm text-[#86868B]">
                Rilancio minimo consigliato: <strong>{formatPrice(result.suggested_bid_increment)}</strong>
              </div>

              {/* Save and Return Button */}
              <div className="pt-4 border-t space-y-3">
                <Button 
                  onClick={handleSaveAndReturn} 
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Salva Valutazione e Torna al Form
                </Button>
                <p className="text-xs text-center text-gray-500">
                  I prezzi calcolati verranno applicati automaticamente al form di creazione immobile
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-[#86868B]">
              <Calculator className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Inserisci i dati dell'immobile per calcolare la valutazione</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Visits Management
const VisitsManagementPage = () => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVisits();
  }, []);

  const fetchVisits = async () => {
    try {
      // Use enhanced endpoint with gara info
      const response = await axios.get('/agent/visits/with-gare');
      setVisits(response.data);
    } catch (error) {
      // Fallback to basic endpoint
      try {
        const fallback = await axios.get('/visits/agent');
        setVisits(fallback.data);
      } catch (e) {
        console.error('Error fetching visits:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (visitId, status) => {
    try {
      await axios.put(`/visits/${visitId}/status?status=${status}`);
      toast.success('Stato aggiornato');
      fetchVisits();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const formatGaraEndTime = (endTime) => {
    if (!endTime) return null;
    try {
      return format(new Date(endTime), "dd/MM 'alle' HH:mm", { locale: it });
    } catch {
      return null;
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6" data-testid="visits-management-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Richieste Visite</h1>
        <div className="flex items-center space-x-2 text-sm text-[#86868B]">
          <Calendar className="h-4 w-4" />
          <span>{visits.length} visite totali</span>
        </div>
      </div>

      {/* Info banner about gara restrictions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <Gavel className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-blue-800">Integrazione Gare di Vendita</p>
          <p className="text-blue-600 mt-1">
            Le visite sono bloccate nelle ultime 2 ore prima della chiusura di una gara di vendita. 
            Gli immobili con gare attive sono evidenziati.
          </p>
        </div>
      </div>

      {visits.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
          <div className="divide-y divide-gray-100">
            {visits.map((visit) => (
              <div key={visit.visit_id} className="p-4" data-testid={`visit-${visit.visit_id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-[#1D1D1F]">{visit.property?.title}</h3>
                    <p className="text-sm text-[#86868B]">{visit.buyer_name} • {visit.buyer_email}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {visit.gara && (
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                        <Gavel className="h-3 w-3 mr-1" />
                        Gara {visit.gara.status === 'active' ? 'Attiva' : 'Programmata'}
                      </Badge>
                    )}
                    <Badge className={
                      visit.status === 'approved' ? 'badge-active' :
                      visit.status === 'pending' ? 'badge-pending' : 'badge-closed'
                    }>
                      {visit.status === 'approved' ? 'Confermata' :
                       visit.status === 'pending' ? 'In attesa' : 'Annullata'}
                    </Badge>
                  </div>
                </div>
                
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <Calendar className="h-4 w-4 mr-1" />
                  {format(new Date(visit.visit_date), 'dd/MM/yyyy', { locale: it })} - {visit.visit_time}
                </div>
                
                {/* Gara info if present */}
                {visit.gara && (
                  <div className="mb-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-purple-700">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>
                          Gara termina: {formatGaraEndTime(visit.gara.end_time) || 'N/A'}
                        </span>
                      </div>
                      <Link 
                        to={`/gara/${visit.gara.competition_id}`}
                        className="text-xs text-purple-600 hover:text-purple-800 flex items-center"
                      >
                        Vai alla gara <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                )}
                
                {visit.status === 'pending' && (
                  <div className="flex space-x-2">
                    <Button size="sm" onClick={() => updateStatus(visit.visit_id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white">
                      <Check className="h-4 w-4 mr-1" /> Conferma
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(visit.visit_id, 'cancelled')} className="text-red-600 border-red-200">
                      <X className="h-4 w-4 mr-1" /> Rifiuta
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-8 text-center">
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-[#86868B]">Nessuna richiesta di visita</p>
        </div>
      )}
    </div>
  );
};

// Participations Management
const ParticipationsManagementPage = () => {
  const [participations, setParticipations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchParticipations();
  }, []);

  const fetchParticipations = async () => {
    try {
      const response = await axios.get('/participation/agent');
      setParticipations(response.data);
    } catch (error) {
      console.error('Error fetching participations:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveParticipation = async (participationId) => {
    try {
      await axios.put(`/participation/${participationId}/approve`);
      toast.success('Partecipazione approvata');
      fetchParticipations();
    } catch (error) {
      toast.error('Errore nell\'approvazione');
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Gestione Partecipazioni</h1>

      {participations.length > 0 ? (
        <div className="space-y-4">
          {participations.map((part) => (
            <div key={part.participation_id} className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-[#1D1D1F]">{part.property?.title}</h3>
                  <p className="text-[#86868B]">{part.buyer_name} • {part.buyer_email}</p>
                </div>
                <Badge className={
                  part.participation_status === 'approved' ? 'badge-active' :
                  part.participation_status === 'pending' ? 'badge-pending' : 'badge-closed'
                }>
                  {part.participation_status}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                <div>
                  <span className="text-[#86868B]">Regole accettate: </span>
                  <span className={part.rules_accepted ? 'text-green-600' : 'text-red-600'}>
                    {part.rules_accepted ? 'Sì' : 'No'}
                  </span>
                </div>
                <div>
                  <span className="text-[#86868B]">Documenti: </span>
                  <span className={part.documents_uploaded ? 'text-green-600' : 'text-[#0071e3]'}>
                    {part.documents_uploaded ? 'Caricati' : 'Da caricare'}
                  </span>
                </div>
                <div>
                  <span className="text-[#86868B]">Caparra: </span>
                  <span className={part.deposit_paid ? 'text-green-600' : 'text-[#0071e3]'}>
                    {part.deposit_paid ? 'Versata' : 'Da versare'}
                  </span>
                </div>
              </div>
              {part.participation_status === 'pending' && (
                <Button onClick={() => approveParticipation(part.participation_id)} className="btn-primary">
                  <Check className="h-4 w-4 mr-2" />
                  Approva Partecipazione
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-8 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-[#86868B]">Nessuna richiesta di partecipazione</p>
        </div>
      )}
    </div>
  );
};

// Available Properties for Agents Page
const AvailablePropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [proposalData, setProposalData] = useState({
    estimated_value: '',
    auction_start_price: '',
    reserve_price: '',
    message: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/agent/available-properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const openProposalDialog = (property) => {
    setSelectedProperty(property);
    setProposalData({
      estimated_value: '',
      auction_start_price: '',
      reserve_price: '',
      message: ''
    });
    setProposalDialogOpen(true);
  };

  const openDetailDialog = (property) => {
    setSelectedProperty(property);
    setDetailDialogOpen(true);
  };

  const handleSubmitProposal = async () => {
    if (!proposalData.estimated_value || !proposalData.auction_start_price) {
      toast.error('Compila i campi obbligatori');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/agent/proposals', {
        property_id: selectedProperty.property_id,
        estimated_value: parseFloat(proposalData.estimated_value),
        auction_start_price: parseFloat(proposalData.auction_start_price),
        reserve_price: parseFloat(proposalData.reserve_price) || parseFloat(proposalData.auction_start_price),
        message: proposalData.message
      });
      toast.success('Proposta inviata con successo!');
      setProposalDialogOpen(false);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della proposta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Immobili Disponibili</h1>
        <p className="text-[#86868B] mt-1">Immobili di proprietari in cerca di un agente per la vendita</p>
      </div>

      {properties.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-6">
          {properties.map((prop) => {
            const photos = prop.photos || prop.documents?.photos || [];
            const hasPhotos = Array.isArray(photos) && photos.length > 0;
            
            // Helper to get valid image URL
            const getPhotoUrl = (photo) => {
              if (!photo) return null;
              if (typeof photo === 'string') {
                if (photo.startsWith('data:') || photo.startsWith('http')) return photo;
                if (photo.startsWith('simulated://')) return null;
              }
              return null;
            };
            
            const coverPhoto = hasPhotos ? getPhotoUrl(photos[0]) : null;
            
            return (
            <div key={prop.property_id} className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`available-property-${prop.property_id}`}>
              {/* Photo Section */}
              {hasPhotos && coverPhoto ? (
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={coverPhoto}
                    alt={prop.address}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {/* Photo count badge */}
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center">
                    <Camera className="h-3 w-3 mr-1" />
                    {photos.length} foto
                  </div>
                  {/* View photos button */}
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute bottom-2 left-2 bg-white/90 hover:bg-white"
                    onClick={() => openDetailDialog(prop)}
                    data-testid={`view-photos-${prop.property_id}`}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Visualizza
                  </Button>
                </div>
              ) : hasPhotos ? (
                /* Photos exist but with invalid URLs (simulated) */
                <div className="relative h-32 bg-gradient-to-br from-[#0071e3]/5 to-[#0071e3]/5 flex items-center justify-center">
                  <div className="text-center text-[#86868B]">
                    <Camera className="h-8 w-8 mx-auto mb-1 text-[#0071e3]" />
                    <span className="text-xs">{photos.length} foto disponibili</span>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute bottom-2 left-2 bg-white/90 hover:bg-white"
                    onClick={() => openDetailDialog(prop)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Visualizza
                  </Button>
                </div>
              ) : (
                <div className="h-32 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <Grid3X3 className="h-8 w-8 mx-auto mb-1" />
                    <span className="text-xs">Nessuna foto</span>
                  </div>
                </div>
              )}
              
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg text-[#1D1D1F]">{prop.address}</h3>
                    <p className="text-[#86868B] text-sm">{prop.city} ({prop.province}), {prop.postal_code}</p>
                  </div>
                  <Badge className="badge-active">Disponibile</Badge>
                </div>
                
                <div className="grid grid-cols-3 gap-3 text-sm text-gray-600 mb-3">
                  {prop.sqm && (
                    <div>
                      <span className="text-gray-400 text-xs">Superficie</span>
                      <p className="font-medium">{prop.sqm} m²</p>
                    </div>
                  )}
                  {prop.rooms && (
                    <div>
                      <span className="text-gray-400 text-xs">Locali</span>
                      <p className="font-medium">{prop.rooms}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-400 text-xs">Tipologia</span>
                    <p className="font-medium capitalize">{prop.property_type}</p>
                  </div>
                </div>

                {prop.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{prop.description}</p>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openDetailDialog(prop)}
                      data-testid={`detail-btn-${prop.property_id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Dettagli
                    </Button>
                  </div>
                  {prop.already_proposed ? (
                    <Badge className="badge-pending">Proposta Inviata</Badge>
                  ) : (
                    <Button 
                      onClick={() => openProposalDialog(prop)} 
                      className="btn-primary"
                      data-testid={`propose-btn-${prop.property_id}`}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Invia Proposta
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )})}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessun immobile disponibile</h3>
          <p className="text-[#86868B]">
            Al momento non ci sono immobili disponibili per proposte
          </p>
        </div>
      )}

      {/* Proposal Dialog */}
      <Dialog open={proposalDialogOpen} onOpenChange={setProposalDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Send className="h-5 w-5 text-[#0071e3]" />
              <span>Invia Proposta di Vendita</span>
            </DialogTitle>
            <DialogDescription>
              Compila i dettagli della tua proposta per gestire la vendita di questo immobile.
            </DialogDescription>
          </DialogHeader>
          
          {selectedProperty && (
            <div className="space-y-6 py-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">{selectedProperty.address}</p>
                <p className="text-sm text-[#86868B]">{selectedProperty.city} ({selectedProperty.province})</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="estimated_value">Valore Stimato (€) *</Label>
                  <Input
                    id="estimated_value"
                    type="number"
                    placeholder="es. 350000"
                    value={proposalData.estimated_value}
                    onChange={(e) => setProposalData({...proposalData, estimated_value: e.target.value})}
                    data-testid="proposal-estimated-value"
                  />
                  <p className="text-xs text-[#86868B] mt-1">Valore di mercato stimato dell'immobile</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="auction_start_price">Prezzo Base Gara (€) *</Label>
                    <Input
                      id="auction_start_price"
                      type="number"
                      placeholder="es. 300000"
                      value={proposalData.auction_start_price}
                      onChange={(e) => setProposalData({...proposalData, auction_start_price: e.target.value})}
                      data-testid="proposal-start-price"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reserve_price">Prezzo Minimo (€)</Label>
                    <Input
                      id="reserve_price"
                      type="number"
                      placeholder="es. 280000"
                      value={proposalData.reserve_price}
                      onChange={(e) => setProposalData({...proposalData, reserve_price: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Messaggio per il Proprietario</Label>
                  <Textarea
                    id="message"
                    placeholder="Descrivi la tua proposta e perché sei il professionista adatto..."
                    rows={3}
                    value={proposalData.message}
                    onChange={(e) => setProposalData({...proposalData, message: e.target.value})}
                  />
                </div>

                <div className="bg-[#0071e3]/10 border border-[#0071e3]/30 rounded-lg p-3">
                  <p className="text-sm text-[#0071e3] font-medium">Commissione Fissa: 2%</p>
                  <p className="text-xs text-gray-600 mt-1">
                    La commissione standard WEQASA è del 2% sul prezzo di vendita finale
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setProposalDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleSubmitProposal} 
                  disabled={submitting}
                  className="btn-primary"
                  data-testid="submit-proposal-btn"
                >
                  {submitting ? 'Invio...' : 'Invia Proposta'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Property Detail Dialog with Photo Gallery */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Building className="h-5 w-5 text-[#0071e3]" />
              <span>Dettaglio Immobile</span>
            </DialogTitle>
            <DialogDescription>
              Visualizza i dettagli e le foto dell'immobile
            </DialogDescription>
          </DialogHeader>
          
          {selectedProperty && (
            <div className="space-y-6 py-4">
              {/* Photo Gallery */}
              <div>
                <h4 className="font-semibold text-[#1D1D1F] mb-3 flex items-center">
                  <Camera className="h-4 w-4 mr-2 text-[#0071e3]" />
                  Foto dell'immobile
                </h4>
                <PhotoGallery 
                  photos={selectedProperty.photos || selectedProperty.documents?.photos || []}
                  propertyTitle={selectedProperty.address}
                  maxThumbnails={4}
                />
              </div>

              {/* Property Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-[#1D1D1F] mb-3">Informazioni Immobile</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#86868B]">Indirizzo</span>
                    <p className="font-medium">{selectedProperty.address}</p>
                  </div>
                  <div>
                    <span className="text-[#86868B]">Città</span>
                    <p className="font-medium">{selectedProperty.city} ({selectedProperty.province})</p>
                  </div>
                  <div>
                    <span className="text-[#86868B]">Tipologia</span>
                    <p className="font-medium capitalize">{selectedProperty.property_type}</p>
                  </div>
                  <div>
                    <span className="text-[#86868B]">Superficie</span>
                    <p className="font-medium">{selectedProperty.sqm || 'N/D'} m²</p>
                  </div>
                  {selectedProperty.rooms && (
                    <div>
                      <span className="text-[#86868B]">Locali</span>
                      <p className="font-medium">{selectedProperty.rooms}</p>
                    </div>
                  )}
                  {selectedProperty.floor !== undefined && (
                    <div>
                      <span className="text-[#86868B]">Piano</span>
                      <p className="font-medium">{selectedProperty.floor}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedProperty.description && (
                <div>
                  <h4 className="font-semibold text-[#1D1D1F] mb-2">Descrizione</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{selectedProperty.description}</p>
                </div>
              )}

              {/* Document Access Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Documenti riservati</p>
                    <p className="text-sm text-amber-700 mt-1">
                      I documenti tecnici e legali (planimetria, visura catastale, atto di provenienza, ecc.) 
                      saranno accessibili solo dopo l'assegnazione del mandato di vendita.
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Chiudi
                </Button>
                {!selectedProperty.already_proposed && (
                  <Button 
                    onClick={() => {
                      setDetailDialogOpen(false);
                      openProposalDialog(selectedProperty);
                    }}
                    className="btn-primary"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Invia Proposta
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Available Property Detail Page (Single property view from notification)
const AvailablePropertyDetailPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [proposalData, setProposalData] = useState({
    estimated_value: '',
    auction_start_price: '',
    reserve_price: '',
    message: ''
  });

  useEffect(() => {
    fetchProperty();
  }, [propertyId]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`/agent/available-properties/${propertyId}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Immobile non trovato');
      navigate('/agent/disponibili');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProposal = async () => {
    if (!proposalData.estimated_value || !proposalData.auction_start_price) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/agent/proposals', {
        property_id: property.property_id,
        estimated_value: parseFloat(proposalData.estimated_value),
        auction_start_price: parseFloat(proposalData.auction_start_price),
        reserve_price: parseFloat(proposalData.reserve_price) || parseFloat(proposalData.auction_start_price),
        message: proposalData.message
      });
      toast.success('Proposta inviata con successo!');
      setProposalDialogOpen(false);
      fetchProperty(); // Refresh to update already_proposed status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della proposta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Immobile non trovato</p>
        <Button variant="outline" onClick={() => navigate('/agent/disponibili')} className="mt-4">
          Torna agli immobili disponibili
        </Button>
      </div>
    );
  }

  const photos = property.photos || property.documents?.photos || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/agent/notifiche')} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-[#1D1D1F]">Dettaglio Immobile</h1>
            <p className="text-[#86868B]">Visualizza i dettagli e invia una proposta</p>
          </div>
        </div>
        <Badge className="badge-active">Disponibile</Badge>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column - Photos */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center">
              <Camera className="h-5 w-5 mr-2 text-[#0071e3]" />
              Foto dell'immobile
            </h2>
            <PhotoGallery 
              photos={photos}
              propertyTitle={property.address}
              maxThumbnails={6}
            />
          </div>
        </div>

        {/* Right Column - Details */}
        <div className="space-y-4">
          {/* Property Info */}
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4">Informazioni Immobile</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-[#1D1D1F]">{property.address}</h3>
                <p className="text-[#86868B] flex items-center mt-1">
                  <MapPin className="h-4 w-4 mr-1" />
                  {property.city} ({property.province}), {property.postal_code}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-[#86868B] text-sm">Tipologia</span>
                  <p className="font-semibold capitalize">{property.property_type}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-[#86868B] text-sm">Superficie</span>
                  <p className="font-semibold">{property.sqm || 'N/D'} m²</p>
                </div>
                {property.rooms && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-[#86868B] text-sm">Locali</span>
                    <p className="font-semibold">{property.rooms}</p>
                  </div>
                )}
                {property.floor !== undefined && property.floor !== null && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-[#86868B] text-sm">Piano</span>
                    <p className="font-semibold">{property.floor}</p>
                  </div>
                )}
              </div>

              {property.description && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-[#1D1D1F] mb-2">Descrizione</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{property.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Document Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Documenti riservati</p>
                <p className="text-sm text-amber-700 mt-1">
                  I documenti tecnici e legali saranno accessibili solo dopo l'assegnazione del mandato di vendita.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            {property.already_proposed ? (
              <div className="text-center py-4">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="font-semibold text-green-700">Proposta già inviata</p>
                <p className="text-sm text-gray-500 mt-1">Hai già inviato una proposta per questo immobile</p>
                <Button variant="outline" onClick={() => navigate('/agent/proposte')} className="mt-4">
                  Vedi le mie proposte
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Interessato a gestire la vendita di questo immobile? Invia una proposta al proprietario.
                </p>
                <Button 
                  onClick={() => setProposalDialogOpen(true)} 
                  className="w-full btn-primary"
                  size="lg"
                >
                  <Send className="h-5 w-5 mr-2" />
                  Invia Proposta di Vendita
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Proposal Dialog */}
      <Dialog open={proposalDialogOpen} onOpenChange={setProposalDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Send className="h-5 w-5 text-[#0071e3]" />
              <span>Invia Proposta di Vendita</span>
            </DialogTitle>
            <DialogDescription>
              Compila i dettagli della tua proposta per gestire la vendita di questo immobile.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-[#86868B]">Immobile</p>
              <p className="font-medium">{property.address}, {property.city}</p>
            </div>

            <div>
              <Label htmlFor="estimated_value">Valore di Mercato Stimato (€) *</Label>
              <Input
                id="estimated_value"
                type="number"
                placeholder="es. 350000"
                value={proposalData.estimated_value}
                onChange={(e) => setProposalData({...proposalData, estimated_value: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="auction_start_price">Prezzo Base Gara (€) *</Label>
                <Input
                  id="auction_start_price"
                  type="number"
                  placeholder="es. 280000"
                  value={proposalData.auction_start_price}
                  onChange={(e) => setProposalData({...proposalData, auction_start_price: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="reserve_price">Prezzo Minimo (€)</Label>
                <Input
                  id="reserve_price"
                  type="number"
                  placeholder="opzionale"
                  value={proposalData.reserve_price}
                  onChange={(e) => setProposalData({...proposalData, reserve_price: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="message">Messaggio al Proprietario</Label>
              <Textarea
                id="message"
                placeholder="Descrivi la tua proposta e perché sei il professionista adatto..."
                rows={3}
                value={proposalData.message}
                onChange={(e) => setProposalData({...proposalData, message: e.target.value})}
              />
            </div>

            <div className="bg-[#0071e3]/10 border border-[#0071e3]/30 rounded-lg p-3">
              <p className="text-sm text-[#0071e3] font-medium">Commissione Fissa: 2%</p>
              <p className="text-xs text-gray-600 mt-1">
                La commissione standard WEQASA è del 2% sul prezzo di vendita finale
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setProposalDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSubmitProposal} 
              disabled={submitting}
              className="btn-primary"
            >
              {submitting ? 'Invio...' : 'Invia Proposta'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// My Proposals Page (Agent)
const MyProposalsPage = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await axios.get('/agent/my-proposals');
      setProposals(response.data);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'In Attesa', class: 'badge-pending' },
      'pending_signature': { label: 'In Firma', class: 'badge-auction' },
      'accepted': { label: 'Accettata', class: 'badge-active' },
      'rejected': { label: 'Rifiutata', class: 'badge-closed' }
    };
    const s = statusMap[status] || { label: status, class: 'badge-pending' };
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Le Mie Proposte</h1>

      {proposals.length > 0 ? (
        <div className="space-y-4">
          {proposals.map((proposal) => (
            <div key={proposal.proposal_id} className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6" data-testid={`proposal-${proposal.proposal_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-[#1D1D1F]">
                    {proposal.property?.address || 'Indirizzo non disponibile'}
                  </h3>
                  <p className="text-[#86868B]">
                    {proposal.property?.city} ({proposal.property?.province})
                  </p>
                </div>
                {getStatusBadge(proposal.status)}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-4">
                <div>
                  <p className="text-sm text-[#86868B]">Valore Stimato</p>
                  <p className="font-semibold text-[#1D1D1F]">{formatPrice(proposal.estimated_value)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Prezzo Base Gara</p>
                  <p className="font-semibold text-[#0071e3]">{formatPrice(proposal.auction_start_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Prezzo Minimo</p>
                  <p className="font-semibold text-[#1D1D1F]">{formatPrice(proposal.reserve_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Commissione</p>
                  <p className="font-semibold text-[#1D1D1F]">{proposal.commission_percentage}%</p>
                </div>
              </div>

              {proposal.message && (
                <div className="p-3 bg-blue-50 rounded-lg mb-4">
                  <p className="text-sm text-blue-800">{proposal.message}</p>
                </div>
              )}

              {proposal.status === 'accepted' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium">
                    Proposta accettata! Il mandato è stato firmato.
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    L'immobile è ora nella tua gestione per la gara di vendita.
                  </p>
                </div>
              )}

              <div className="text-xs text-gray-400 mt-4">
                Inviata il: {new Date(proposal.created_at).toLocaleDateString('it-IT')}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <Send className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna proposta inviata</h3>
          <p className="text-[#86868B] mb-6">Non hai ancora inviato proposte per immobili</p>
          <Link to="/agent/disponibili">
            <Button className="btn-primary">
              <MapPin className="h-4 w-4 mr-2" />
              Vedi Immobili Disponibili
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

// Subscription Page
const SubscriptionPage = () => {
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const location = useLocation();

  // Hardcoded plans as per requirements
  const plans = [
    {
      id: 'base',
      name: 'Piano Base',
      amount: 99,
      interval: 'mese',
      recommended: false,
      features: [
        'Gestione gara di vendita',
        'Proposte limitate'
      ]
    },
    {
      id: 'premium',
      name: 'Piano Premium',
      amount: 250,
      interval: 'mese',
      recommended: true,
      features: [
        'Accesso completo alla piattaforma',
        'Proposte illimitate',
        'Gestione gara di vendita',
        'Supporto prioritario',
        'Nessun vincolo – disdici quando vuoi'
      ]
    },
    {
      id: 'enterprise',
      name: 'Piano Enterprise',
      amount: 450,
      interval: 'mese',
      recommended: false,
      features: [
        'Accesso completo alla piattaforma',
        'Virtual tour AI',
        'Valore strategico WQ',
        'Sito premium con dominio personalizzato',
        'Proposte illimitate',
        'Gestione gara di vendita',
        'Supporto prioritario',
        'Nessun vincolo – disdici quando vuoi'
      ]
    }
  ];

  useEffect(() => {
    fetchData();
    // Check for payment status in URL
    const params = new URLSearchParams(location.search);
    if (params.get('payment') === 'success') {
      toast.success('Abbonamento attivato con successo!');
      window.history.replaceState({}, '', '/agent/abbonamento');
    } else if (params.get('payment') === 'cancelled') {
      toast.error('Pagamento annullato');
      window.history.replaceState({}, '', '/agent/abbonamento');
    }
  }, [location]);

  const fetchData = async () => {
    try {
      const statusRes = await axios.get('/agent/subscription/status');
      setSubscriptionStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(true);
    try {
      const plan = plans.find(p => p.id === planId);
      const response = await axios.post('/payments/subscription/checkout', {
        plan: planId,
        amount: plan.amount,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Errore nella creazione della sessione di pagamento');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'attivazione dell\'abbonamento');
    } finally {
      setSubscribing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR', 
      minimumFractionDigits: 0 
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#1D1D1F]">Abbonamento</h1>
        <p className="text-[#86868B] mt-1">Scegli il piano più adatto alle tue esigenze</p>
      </div>

      {/* Current Subscription Status */}
      {subscriptionStatus?.has_subscription ? (
        <div className="bg-gradient-to-r from-[#0071e3] to-purple-600 rounded-xl p-6 text-white" data-testid="active-subscription">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                <Crown className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Abbonamento Attivo</h2>
                <p className="text-white/80 text-sm mt-1">
                  {subscriptionStatus.subscription?.plan_name || 'Piano Premium'}
                </p>
              </div>
            </div>
            <Badge className="bg-white/20 text-white border-none">
              <CheckCircle className="h-3 w-3 mr-1" />
              Attivo
            </Badge>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-white/70 text-xs">Stato</p>
              <p className="font-medium capitalize">{subscriptionStatus.subscription?.subscription_status || 'active'}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-white/70 text-xs">Importo Mensile</p>
              <p className="font-medium">{formatPrice(subscriptionStatus.subscription?.amount || 250)}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-white/70 text-xs">Data Attivazione</p>
              <p className="font-medium">{formatDate(subscriptionStatus.subscription?.created_at)}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-white/70 text-xs">Prossimo Rinnovo</p>
              <p className="font-medium">{formatDate(subscriptionStatus.subscription?.next_billing_date)}</p>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-white/20">
            <p className="text-sm text-white/80">
              Il tuo abbonamento si rinnoverà automaticamente. Per disdire o modificare il piano, contatta il supporto WEQASA.
            </p>
          </div>
        </div>
      ) : (
        /* No Active Subscription - Show Plans */
        <div className="space-y-6">
          {/* Subscription Plans - 3 Column Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl border-2 shadow-lg overflow-hidden relative transition-transform hover:scale-[1.02] ${
                  plan.recommended 
                    ? 'border-[#0071e3] ring-2 ring-[#0071e3]/20' 
                    : 'border-gray-200'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {/* Recommended Badge */}
                {plan.recommended && (
                  <div className="absolute top-0 right-0 bg-[#0071e3] text-white px-4 py-1 text-xs font-semibold rounded-bl-xl">
                    Consigliato WQ
                  </div>
                )}

                {/* Plan Header */}
                <div className={`p-6 ${
                  plan.recommended 
                    ? 'bg-gradient-to-r from-[#0071e3] to-purple-600 text-white' 
                    : plan.id === 'enterprise' 
                      ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white'
                      : 'bg-gray-50'
                }`}>
                  <h3 className={`text-xl font-bold ${plan.recommended || plan.id === 'enterprise' ? 'text-white' : 'text-[#1D1D1F]'}`}>
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline mt-3">
                    <span className={`text-4xl font-bold ${plan.recommended || plan.id === 'enterprise' ? 'text-white' : 'text-[#1D1D1F]'}`}>
                      {formatPrice(plan.amount)}
                    </span>
                    <span className={`ml-2 ${plan.recommended || plan.id === 'enterprise' ? 'text-white/70' : 'text-gray-500'}`}>
                      /{plan.interval}
                    </span>
                  </div>
                </div>

                {/* Plan Features */}
                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                          plan.recommended ? 'text-[#0071e3]' : 'text-green-500'
                        }`} />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing}
                    className={`w-full py-5 text-base font-semibold ${
                      plan.recommended 
                        ? 'bg-[#0071e3] hover:bg-[#0066cc] text-white' 
                        : plan.id === 'enterprise'
                          ? 'bg-gray-800 hover:bg-gray-900 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-[#1D1D1F]'
                    }`}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {subscribing ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Elaborazione...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-5 w-5 mr-2" />
                        Attiva {plan.name}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Benefits Section */}
          <div className="bg-gradient-to-br from-[#0071e3]/5 to-purple-500/5 rounded-xl p-6 border border-[#0071e3]/10">
            <div className="flex items-center space-x-3 mb-4">
              <Crown className="h-6 w-6 text-[#0071e3]" />
              <h2 className="text-lg font-semibold text-[#1D1D1F]">Perché scegliere WEQASA?</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { icon: MapPin, text: 'Accesso a immobili di proprietari qualificati' },
                { icon: Building, text: 'Sistema di gara di vendita innovativo' },
                { icon: Shield, text: 'Transazioni sicure e trasparenti' },
                { icon: Calculator, text: 'Strumento di valutazione OMI avanzato' },
                { icon: Video, text: 'Virtual tour AI (Piano Enterprise)' },
                { icon: BarChart3, text: 'Report e statistiche dettagliate' }
              ].map((benefit, idx) => (
                <div key={idx} className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-[#0071e3]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="h-4 w-4 text-[#0071e3]" />
                  </div>
                  <span className="text-gray-700 text-sm">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Security & Payment Info */}
          <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>Pagamento sicuro con Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span>Disdici quando vuoi</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span>Carta di credito o debito</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Agent Notifications Page with Tabs
const AgentNotificationsPage = () => {
  const [activeTab, setActiveTab] = useState('proposals'); // 'proposals' or 'general'
  const [notifications, setNotifications] = useState([]);
  const [sellerProposals, setSellerProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    fetchSellerProposals();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/agent/notifications');
      // Filter only general notifications (not seller proposals)
      const generalNotifs = response.data.filter(n => n.type !== 'seller_proposal');
      setNotifications(generalNotifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSellerProposals = async () => {
    try {
      const response = await axios.get('/agent/seller-proposals');
      setSellerProposals(response.data);
    } catch (error) {
      console.error('Error fetching seller proposals:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`/agent/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put('/agent/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success('Tutte le notifiche segnate come lette');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) {
      markAsRead(notif.notification_id);
    }
    if (notif.data?.property_id) {
      navigate(`/agent/disponibili/${notif.data.property_id}`);
    }
  };

  const handleProposalClick = (proposal) => {
    navigate(`/agent/proposte-venditori/${proposal.proposal_id}`);
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'verification_complete':
        return <Shield className="h-5 w-5 text-green-500" />;
      case 'subscription_active':
        return <CreditCard className="h-5 w-5 text-blue-500" />;
      case 'proposal_accepted':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'proposal_rejected':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'mandate_ready':
        return <FileText className="h-5 w-5 text-indigo-500" />;
      case 'system':
        return <Bell className="h-5 w-5 text-amber-500" />;
      default:
        return <Bell className="h-5 w-5 text-[#86868B]" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return format(date, 'dd/MM/yyyy', { locale: it });
  };

  const unreadNotifications = notifications.filter(n => !n.is_read).length;
  const pendingProposals = sellerProposals.filter(p => p.status === 'pending').length;

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6" data-testid="agent-notifications-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#1D1D1F]">Centro Notifiche</h1>
        <p className="text-[#86868B] mt-1">Gestisci le notifiche e le proposte dei venditori</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setActiveTab('proposals')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors relative ${
              activeTab === 'proposals' 
                ? 'text-[#0071e3] border-b-2 border-[#0071e3] bg-[#0071e3]/5' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Home className="h-5 w-5" />
              <span>Proposte Venditori</span>
              {pendingProposals > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingProposals}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('general')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors relative ${
              activeTab === 'general' 
                ? 'text-[#0071e3] border-b-2 border-[#0071e3] bg-[#0071e3]/5' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Bell className="h-5 w-5" />
              <span>Notifiche Generali</span>
              {unreadNotifications > 0 && (
                <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {unreadNotifications}
                </span>
              )}
            </div>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Seller Proposals Tab */}
          {activeTab === 'proposals' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  Immobili proposti dai proprietari per la tua valutazione
                </p>
              </div>

              {sellerProposals.length > 0 ? (
                <div className="space-y-4">
                  {sellerProposals.map((proposal) => (
                    <div 
                      key={proposal.proposal_id}
                      className={`border rounded-xl p-4 cursor-pointer hover:shadow-md transition-all ${
                        proposal.status === 'pending' 
                          ? 'border-[#0071e3] bg-[#0071e3]/5' 
                          : 'border-gray-200'
                      }`}
                      onClick={() => handleProposalClick(proposal)}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          proposal.status === 'pending' ? 'bg-[#0071e3]/10' : 'bg-gray-100'
                        }`}>
                          {proposal.photos && proposal.photos.length > 0 ? (
                            <img src={proposal.photos[0]} alt="Property" className="w-full h-full object-cover rounded-xl" />
                          ) : (
                            <Building className={`h-8 w-8 ${proposal.status === 'pending' ? 'text-[#0071e3]' : 'text-gray-400'}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-[#1D1D1F]">{proposal.address}</h3>
                              <p className="text-sm text-gray-600">{proposal.city} ({proposal.province})</p>
                            </div>
                            <Badge className={
                              proposal.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                              proposal.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              proposal.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {proposal.status === 'pending' ? 'In Attesa' :
                               proposal.status === 'accepted' ? 'Accettata' :
                               proposal.status === 'rejected' ? 'Rifiutata' : proposal.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Home className="h-4 w-4" />
                              {proposal.property_type || 'Appartamento'}
                            </span>
                            {proposal.sqm && (
                              <span>{proposal.sqm} m²</span>
                            )}
                            {proposal.rooms && (
                              <span>{proposal.rooms} locali</span>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <User className="h-3 w-3" />
                              <span>Proposto da: {proposal.seller_name || 'Proprietario'}</span>
                            </div>
                            <span className="text-xs text-gray-400">{formatDate(proposal.created_at)}</span>
                          </div>
                          {proposal.status === 'pending' && (
                            <div className="mt-3 pt-3 border-t border-dashed">
                              <Button size="sm" className="btn-primary">
                                <Calculator className="h-4 w-4 mr-2" />
                                Valuta Immobile
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Home className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna proposta</h3>
                  <p className="text-[#86868B]">
                    Quando un proprietario ti propone il suo immobile, apparirà qui
                  </p>
                </div>
              )}
            </div>
          )}

          {/* General Notifications Tab */}
          {activeTab === 'general' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  Notifiche amministrative e aggiornamenti da WEQASA
                </p>
                {unreadNotifications > 0 && (
                  <Button variant="outline" size="sm" onClick={markAllAsRead}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Segna tutte come lette
                  </Button>
                )}
              </div>

              {notifications.length > 0 ? (
                <div className="divide-y divide-gray-100 border rounded-xl overflow-hidden">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.notification_id} 
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !notif.is_read ? 'bg-blue-50/50 border-l-4 border-l-blue-500' : ''
                      }`}
                      onClick={() => handleNotificationClick(notif)}
                      data-testid={`notification-${notif.notification_id}`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          !notif.is_read ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          {getNotificationIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className={`font-medium ${!notif.is_read ? 'text-[#1D1D1F]' : 'text-gray-700'}`}>
                                {notif.title}
                              </p>
                              <p className="text-gray-600 text-sm mt-1">{notif.message}</p>
                            </div>
                            {!notif.is_read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-2">
                            {formatDate(notif.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna notifica</h3>
                  <p className="text-[#86868B]">Le notifiche generali appariranno qui</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Reports Page
const ReportsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [period]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/agent/reports/stats?period=${period}`);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`/agent/reports/pdf?period=${period}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_agente_${period}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report scaricato con successo');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Errore nel download del report');
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(value || 0);
  };

  const periodLabels = {
    week: 'Ultima settimana',
    month: 'Ultimo mese',
    quarter: 'Ultimo trimestre',
    year: 'Ultimo anno',
    all: 'Storico completo'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Report & Statistiche</h1>
          <p className="text-[#86868B] mt-1">Monitora le tue performance e risultati</p>
        </div>
        <div className="flex items-center space-x-3">
          <select 
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm"
          >
            {Object.entries(periodLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <Button onClick={downloadPDF} disabled={downloading} variant="outline">
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Scarica PDF
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[#86868B] uppercase tracking-wide">Proposte Inviate</p>
              <p className="text-2xl font-semibold text-[#1D1D1F] mt-1">{stats?.total_proposals || 0}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Send className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-[#86868B] mt-2">
            <span className="text-green-600">{stats?.proposals_in_period || 0}</span> nel periodo
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[#86868B] uppercase tracking-wide">Tasso Accettazione</p>
              <p className="text-2xl font-bold text-[#0071e3] mt-1">{stats?.acceptance_rate || 0}%</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Target className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-[#86868B] mt-2">
            <span className="text-green-600">{stats?.accepted_proposals || 0}</span> accettate
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[#86868B] uppercase tracking-wide">Vendite Concluse</p>
              <p className="text-2xl font-semibold text-[#1D1D1F] mt-1">{stats?.completed_sales || 0}</p>
            </div>
            <div className="w-10 h-10 bg-[#0071e3]/20 rounded-full flex items-center justify-center">
              <Award className="h-5 w-5 text-[#0071e3]" />
            </div>
          </div>
          <p className="text-xs text-[#86868B] mt-2">
            <span className="text-green-600">{stats?.sales_in_period || 0}</span> nel periodo
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[#86868B] uppercase tracking-wide">Rating Medio</p>
              <div className="flex items-center mt-1">
                <p className="text-2xl font-semibold text-[#1D1D1F]">{stats?.avg_rating || 0}</p>
                <Star className="h-5 w-5 text-yellow-400 fill-yellow-400 ml-1" />
              </div>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <Star className="h-5 w-5 text-[#0071e3]" />
            </div>
          </div>
          <p className="text-xs text-[#86868B] mt-2">
            su {stats?.total_reviews || 0} recensioni
          </p>
        </div>
      </div>

      {/* Revenue Section */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-[#0071e3] to-[#1A6B7C] rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Valore Vendite Totale</h3>
            <DollarSign className="h-6 w-6 text-white/70" />
          </div>
          <p className="text-3xl font-bold">{formatCurrency(stats?.total_sales_value)}</p>
          <p className="text-white/70 text-sm mt-2">Valore complessivo immobili venduti</p>
        </div>

        <div className="bg-gradient-to-br from-[#0071e3] to-[#3399FF] rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Commissioni Totali</h3>
            <TrendingUp className="h-6 w-6 text-white/70" />
          </div>
          <p className="text-3xl font-bold">{formatCurrency(stats?.total_commissions)}</p>
          <p className="text-white/70 text-sm mt-2">Guadagni da commissioni</p>
        </div>
      </div>

      {/* Activity Summary */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h3 className="font-semibold text-[#1D1D1F] mb-4">Riepilogo Attività</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <Building className="h-6 w-6 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#1D1D1F]">{stats?.total_properties || 0}</p>
              <p className="text-sm text-[#86868B]">Immobili gestiti</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-6 w-6 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#1D1D1F]">{stats?.total_visits || 0}</p>
              <p className="text-sm text-[#86868B]">Visite totali</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#1D1D1F]">{stats?.completed_sales || 0}</p>
              <p className="text-sm text-[#86868B]">Mandati firmati</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rating Section */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#1D1D1F]">Le Tue Recensioni</h3>
          <Link to="/agent/recensioni" className="text-sm text-[#0071e3] hover:underline flex items-center">
            Vedi tutte <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="flex items-center space-x-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1">
              <span className="text-4xl font-semibold text-[#1D1D1F]">{stats?.avg_rating || 0}</span>
              <Star className="h-8 w-8 text-yellow-400 fill-yellow-400" />
            </div>
            <p className="text-sm text-[#86868B] mt-1">Rating medio</p>
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              {[5, 4, 3, 2, 1].map((star) => (
                <div key={star} className="flex items-center space-x-1">
                  <span className="text-xs text-[#86868B]">{star}</span>
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                </div>
              ))}
            </div>
            <p className="text-sm text-[#86868B] mt-2">
              Basato su {stats?.total_reviews || 0} recensioni dei proprietari
            </p>
          </div>
        </div>
      </div>

      {/* Gare di Vendita Stats */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6" data-testid="gare-stats-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#1D1D1F]">Gare di Vendita</h3>
          <Badge variant="outline" className="text-[#0071e3] border-[#0071e3]">
            <Gavel className="h-3 w-3 mr-1" />
            Competizioni
          </Badge>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{stats?.gare_active || 0}</p>
            <p className="text-xs text-gray-600 mt-1">Gare Attive</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{stats?.gare_completed || 0}</p>
            <p className="text-xs text-gray-600 mt-1">Gare Concluse</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{stats?.gare_total_bids || 0}</p>
            <p className="text-xs text-gray-600 mt-1">Offerte Totali</p>
          </div>
          <div className="text-center p-4 bg-amber-50 rounded-lg">
            <p className="text-2xl font-bold text-amber-600">{stats?.gare_avg_bids || 0}</p>
            <p className="text-xs text-gray-600 mt-1">Media Offerte/Gara</p>
          </div>
        </div>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="p-4 border border-gray-100 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Valore Gare Attive</span>
              <span className="font-semibold text-[#1D1D1F]">{formatCurrency(stats?.gare_total_value)}</span>
            </div>
          </div>
          <div className="p-4 border border-gray-100 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Valore Aggiudicazioni</span>
              <span className="font-semibold text-green-600">{formatCurrency(stats?.gare_won_value)}</span>
            </div>
          </div>
        </div>
        {stats?.gare_deposit_conversions > 0 && (
          <div className="mt-3 flex items-center text-sm text-[#86868B]">
            <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
            {stats.gare_deposit_conversions} depositi versati dai partecipanti
          </div>
        )}
      </div>
    </div>
  );
};

// Messages Page - Agent chat with property owners
const MessagesPage = () => {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);

  useEffect(() => {
    fetchChats();
  }, []);

  const fetchChats = async () => {
    try {
      const response = await axios.get('/chat/my-chats');
      setChats(response.data);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'HH:mm', { locale: it });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agent-messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Messaggi</h1>
          <p className="text-[#86868B] mt-1">Comunicazioni con i proprietari degli immobili</p>
        </div>
      </div>

      {selectedChat ? (
        <div className="space-y-4">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedChat(null)}
            className="mb-2"
          >
            <ChevronRight className="h-4 w-4 mr-1 rotate-180" />
            Torna alla lista
          </Button>
          
          <div className="bg-gradient-to-r from-[#0071e3] to-[#1A6B7C] rounded-lg p-4 text-white">
            <div className="flex items-center space-x-3">
              <Building className="h-6 w-6" />
              <div>
                <h2 className="font-semibold">{selectedChat.property?.title || 'Immobile'}</h2>
                <p className="text-sm text-white/80">
                  {selectedChat.property?.city} • Proprietario: {selectedChat.seller_name}
                </p>
              </div>
            </div>
          </div>
          
          <PropertyChat 
            propertyId={selectedChat.property_id}
            userType="agent"
            compact={false}
          />
        </div>
      ) : (
        <>
          {chats.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
              <div className="divide-y divide-gray-100">
                {chats.map((chat) => (
                  <button
                    key={chat.chat_id}
                    onClick={() => setSelectedChat(chat)}
                    className="w-full p-4 flex items-center space-x-4 hover:bg-gray-50 transition-colors text-left"
                    data-testid={`chat-${chat.chat_id}`}
                  >
                    <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {chat.property?.photos?.[0] ? (
                        <img 
                          src={chat.property.photos[0].url || chat.property.photos[0]} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Building className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-[#1D1D1F] truncate">
                          {chat.property?.title || 'Immobile'}
                        </h3>
                        {chat.last_message_at && (
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                            {formatTime(chat.last_message_at)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {chat.seller_name} • {chat.property?.city}
                      </p>
                      <p className="text-sm text-gray-400 truncate mt-0.5">
                        {chat.last_message_preview || 'Nessun messaggio'}
                      </p>
                    </div>
                    {chat.unread_agent > 0 && (
                      <Badge className="bg-[#0071e3] text-white flex-shrink-0">
                        {chat.unread_agent}
                      </Badge>
                    )}
                    <ChevronRight className="h-5 w-5 text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
              <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna conversazione</h3>
              <p className="text-[#86868B] text-sm max-w-md mx-auto">
                Le chat verranno attivate automaticamente quando un proprietario firmerà un mandato 
                con te per la vendita di un immobile.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Profile Page
const AgentProfilePage = () => {
  const { user } = useAuth();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Profilo Azienda</h1>
      
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-[#86868B]">Ragione Sociale</label>
            <p className="font-medium text-[#1D1D1F]">{user?.company_name}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Partita IVA</label>
            <p className="font-medium text-[#1D1D1F]">{user?.vat_number}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Numero REA</label>
            <p className="font-medium text-[#1D1D1F]">{user?.rea_number}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Camera di Commercio</label>
            <p className="font-medium text-[#1D1D1F]">{user?.chamber_of_commerce}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">PEC</label>
            <p className="font-medium text-[#1D1D1F]">{user?.certified_email}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Sede Legale</label>
            <p className="font-medium text-[#1D1D1F]">{user?.registered_address}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Email</label>
            <p className="font-medium text-[#1D1D1F]">{user?.email}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Telefono</label>
            <p className="font-medium text-[#1D1D1F]">{user?.phone}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Security Page with 2FA
const AgentSecurityPage = () => {
  const { token } = useAuth();

  return (
    <div className="space-y-8" data-testid="agent-security-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#1D1D1F] tracking-tight mb-2">Sicurezza</h1>
        <p className="text-[#86868B]">Gestisci le impostazioni di sicurezza del tuo account</p>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
        <div className="p-6 border-b border-[#E5E5EA]">
          <h2 className="text-lg font-semibold text-[#1D1D1F] flex items-center space-x-2">
            <Shield className="h-5 w-5 text-[#0071e3]" />
            <span>Autenticazione a due fattori (2FA)</span>
          </h2>
          <p className="text-sm text-[#86868B] mt-1">
            Aggiungi un ulteriore livello di sicurezza al tuo account
          </p>
        </div>
        <div className="p-6">
          <TwoFactorSetup token={token} />
        </div>
      </div>

      <Link 
        to="/agent/privacy"
        className="block bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-[#0071e3]/10 flex items-center justify-center">
              <User className="h-6 w-6 text-[#0071e3]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1D1D1F]">Privacy e dati personali</h3>
              <p className="text-sm text-[#86868B]">Gestisci i tuoi dati e le preferenze GDPR</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[#86868B]" />
        </div>
      </Link>
    </div>
  );
};

// Privacy Dashboard Page
const PrivacyDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    marketing_emails: false,
    analytics_tracking: true,
    third_party_sharing: false,
    personalized_content: true
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const handlePreferenceChange = async (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    toast.success('Preferenza aggiornata');
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      // Simulate data export request
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('Richiesta di esportazione dati inviata. Riceverai un\'email con il link per scaricare i tuoi dati.');
      setShowExportDialog(false);
    } catch (error) {
      toast.error('Errore nella richiesta di esportazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      // Simulate account deletion request
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('Richiesta di eliminazione account inviata. Sarai contattato per confermare l\'operazione.');
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Errore nella richiesta di eliminazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/agent/sicurezza')} className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-[#1D1D1F]">Privacy e Dati Personali</h1>
          <p className="text-[#86868B] mt-1">Gestisci i tuoi dati secondo il GDPR (Reg. UE 2016/679)</p>
        </div>
      </div>

      {/* GDPR Rights Info */}
      <div className="bg-[#0071e3]/5 border border-[#0071e3]/20 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <Shield className="h-8 w-8 text-[#0071e3] flex-shrink-0" />
          <div>
            <h2 className="font-semibold text-[#1D1D1F] mb-2">I tuoi diritti secondo il GDPR</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              In qualità di cittadino dell'Unione Europea, hai diritto di accedere, rettificare, cancellare 
              e portare i tuoi dati personali. Puoi anche opporti al trattamento e revocare il consenso in qualsiasi momento.
            </p>
          </div>
        </div>
      </div>

      {/* Data Overview */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#0071e3]" />
          Dati in nostro possesso
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati identificativi</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Nome e cognome</li>
              <li>• Indirizzo email: {user?.email}</li>
              <li>• Numero di telefono (se fornito)</li>
              <li>• Indirizzo (se fornito)</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati professionali</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Nome agenzia</li>
              <li>• Partita IVA</li>
              <li>• Numero REA</li>
              <li>• Licenza immobiliare</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati di utilizzo</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Log di accesso</li>
              <li>• Immobili gestiti</li>
              <li>• Proposte inviate</li>
              <li>• Cronologia messaggi</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati di pagamento</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Metodo di pagamento (parzialmente mascherato)</li>
              <li>• Storico fatture</li>
              <li>• Stato abbonamento</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Consent Preferences */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-[#0071e3]" />
          Preferenze di consenso
        </h2>
        <div className="space-y-4">
          {[
            {
              key: 'marketing_emails',
              title: 'Email di marketing',
              description: 'Ricevi aggiornamenti su nuove funzionalità, promozioni e novità WEQASA',
              required: false
            },
            {
              key: 'analytics_tracking',
              title: 'Analytics e miglioramento servizio',
              description: 'Ci permette di analizzare l\'uso della piattaforma per migliorare l\'esperienza',
              required: false
            },
            {
              key: 'third_party_sharing',
              title: 'Condivisione con terze parti',
              description: 'Condivisione dati con partner selezionati per servizi aggiuntivi',
              required: false
            },
            {
              key: 'personalized_content',
              title: 'Contenuti personalizzati',
              description: 'Mostra immobili e suggerimenti basati sulle tue preferenze',
              required: false
            }
          ].map((pref) => (
            <div key={pref.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
              <div className="flex-1">
                <h3 className="font-medium text-[#1D1D1F]">{pref.title}</h3>
                <p className="text-sm text-gray-500">{pref.description}</p>
              </div>
              <button
                onClick={() => handlePreferenceChange(pref.key, !preferences[pref.key])}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  preferences[pref.key] ? 'bg-[#0071e3]' : 'bg-gray-300'
                }`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  preferences[pref.key] ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Data Actions */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
          <Download className="h-5 w-5 text-[#0071e3]" />
          Azioni sui tuoi dati
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Export Data */}
          <div className="border border-gray-200 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Esporta i tuoi dati</h3>
            <p className="text-sm text-gray-500 mb-4">
              Scarica una copia di tutti i dati che abbiamo su di te in formato leggibile (Art. 20 GDPR - Diritto alla portabilità)
            </p>
            <Button variant="outline" onClick={() => setShowExportDialog(true)}>
              <Download className="h-4 w-4 mr-2" />
              Richiedi esportazione
            </Button>
          </div>

          {/* Delete Account */}
          <div className="border border-red-200 rounded-xl p-4 bg-red-50/50">
            <h3 className="font-medium text-red-800 mb-2">Elimina account e dati</h3>
            <p className="text-sm text-red-600 mb-4">
              Elimina definitivamente il tuo account e tutti i dati associati (Art. 17 GDPR - Diritto all'oblio)
            </p>
            <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-100" onClick={() => setShowDeleteDialog(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Richiedi eliminazione
            </Button>
          </div>
        </div>
      </div>

      {/* Legal References */}
      <div className="bg-gray-50 rounded-2xl p-6">
        <h2 className="font-semibold text-[#1D1D1F] mb-4">Riferimenti normativi</h2>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <h3 className="font-medium mb-2">Regolamento GDPR</h3>
            <ul className="text-gray-600 space-y-1">
              <li>• Art. 15 - Diritto di accesso</li>
              <li>• Art. 16 - Diritto di rettifica</li>
              <li>• Art. 17 - Diritto alla cancellazione</li>
              <li>• Art. 20 - Diritto alla portabilità</li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium mb-2">Contatti DPO</h3>
            <p className="text-gray-600">
              Per esercitare i tuoi diritti o per domande sulla privacy:<br />
              <strong>Email:</strong> privacy@weqasa.it<br />
              <strong>PEC:</strong> privacy@pec.weqasa.it
            </p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Titolare del trattamento: WEQASA S.r.l. - Via [Indirizzo], [CAP] [Città] - P.IVA [XXXXXXXXXXX]
          </p>
        </div>
      </div>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Esporta i tuoi dati</DialogTitle>
            <DialogDescription>
              Riceverai un'email con un link per scaricare tutti i tuoi dati personali in formato JSON/CSV.
              Il link sarà valido per 7 giorni.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              L'esportazione includerà:
            </p>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Dati del profilo</li>
              <li>• Storico attività</li>
              <li>• Immobili e proposte</li>
              <li>• Messaggi e comunicazioni</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Annulla</Button>
            <Button onClick={handleExportData} disabled={loading} className="btn-primary">
              {loading ? 'Elaborazione...' : 'Conferma esportazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Elimina account e dati</DialogTitle>
            <DialogDescription>
              Questa azione è irreversibile. Tutti i tuoi dati saranno eliminati definitivamente.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium">Attenzione:</p>
              <ul className="mt-2 text-sm text-red-700 space-y-1">
                <li>• Perderai l'accesso a tutti i tuoi immobili</li>
                <li>• Le proposte in corso saranno annullate</li>
                <li>• I messaggi saranno eliminati</li>
                <li>• L'abbonamento sarà cancellato senza rimborso</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annulla</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount} 
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? 'Elaborazione...' : 'Conferma eliminazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Agent Dashboard
const AgentDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <PropertyFormProvider>
      <div className="min-h-screen bg-white">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        
        {/* Email Verification Banner */}
        <div className="lg:ml-72">
          <EmailVerificationBanner user={user} />
        </div>
        
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-30 glass-header px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2 rounded-xl hover:bg-[#F5F5F7] transition-colors">
              <Menu className="h-6 w-6 text-[#1D1D1F]" strokeWidth={1.5} />
            </button>
            <span className="text-lg font-semibold text-[#1D1D1F] tracking-tight">WEQASA</span>
            <div className="w-10" />
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:ml-72 p-6 lg:p-10">
          <Routes>
            <Route index element={<AgentOverview />} />
            <Route path="profilo-agenzia" element={<AgencyProfileEditor />} />
            <Route path="immobili" element={<PropertiesPage />} />
            <Route path="immobili/nuovo" element={<CreatePropertyPage />} />
            <Route path="nuovo-immobile" element={<CreatePropertyPage />} />
            <Route path="immobili/:propertyId" element={<CreatePropertyPage />} />
            <Route path="disponibili" element={<AvailablePropertiesPage />} />
            <Route path="disponibili/:propertyId" element={<AvailablePropertyDetailPage />} />
            <Route path="proposte" element={<MyProposalsPage />} />
            <Route path="messaggi" element={<MessagesPage />} />
            <Route path="valutazione" element={<OMIValuationPage />} />
            <Route path="visite" element={<VisitsManagementPage />} />
            <Route path="partecipazioni" element={<ParticipationsManagementPage />} />
            <Route path="notifiche" element={<AgentNotificationsPage />} />
            <Route path="abbonamento" element={<SubscriptionPage />} />
            <Route path="report" element={<ReportsPage />} />
            <Route path="profilo" element={<AgentProfilePage />} />
            <Route path="sicurezza" element={<AgentSecurityPage />} />
            <Route path="privacy" element={<PrivacyDashboardPage />} />
          </Routes>
        </div>
      </div>
    </PropertyFormProvider>
  );
};

export default AgentDashboard;
